import { useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  UrlUpdateType,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { PlotParentTypes, PlotValueTypes } from "../config";
import {
  RasterWeatherLayer,
  VectorWeatherLayer,
  WeatherMapLayers,
  UIContextDefaults,
} from "../contexts/UIContext";

export type useAppUrlResult = {
  weatherMapLayers: WeatherMapLayers;
  weatherPlotQuantity: PlotValueTypes;
  currentSimulatorTime: Date;
  timeAtPageLoad: Date;
  simulatedHistoricalTime?: Date;

  setWeatherMapLayers: (weatherMapLayers: WeatherMapLayers) => void;
  setWeatherPlotQuantity: (
    plotQuantity: PlotValueTypes | PlotParentTypes
  ) => void;
  setCurrentSimulatorTime: (time: Date | undefined) => void;
  setSimulatedHistoricalTime: (simulatedHistoricalTime: string) => void;
};

// use defaults from the UIcontext, set these only once

const weatherDefault = UIContextDefaults.weatherMapLayers;

const weatherMapLayerRasterDefault = weatherDefault.raster;
const weatherMapLayerVectorDefault = weatherDefault.vector;
const weatherMapLayerVectorDisplayDefault = weatherDefault.showVectors;
const weatherMapLayerPressureDefault = weatherDefault.showPressure;
const weatherMapLayerPressureCenterDefault = weatherDefault.showPressureCenters;
const weatherMapLayerShowSpottersDefault = weatherDefault.showSpotters;
const weatherMapLayerColorSpottersDefault = weatherDefault.colorSpotters;
const weatherMapLayerShowTropicalStormsDefault =
  weatherDefault.showTropicalStorms;
const plotQuantityDefault = weatherDefault.raster;
const simulatedHistoricalTimeDefault = "";

type WeatherQuery = {
  showSpotters?: boolean;
  colorSpotters?: boolean;
  showVectors?: boolean;
  showPressure?: boolean;
  showPressureCenters?: boolean;
  showTropicalStorms?: boolean;
  raster?: RasterWeatherLayer;
  vector?: VectorWeatherLayer;
  plotQuantity?: string;
  currentSimulatorTime?: number;
  simulatedHistoricalTime?: string;
};

/**
 * Returns the standard URL parameters @see UrlParams and any params
 * that are passed as the generic argument.
 */
const useAppUrl = <P = {}>(): useAppUrlResult & P => {
  // FIXME intersecting the return type with the generic in the definition of the component lets you presume this function will return anything you want
  // WARNING: Should you get an error along the lines of: "Cannot read property 'match' of undefined" when developing storybook stories, and you traced this here,
  // then you need to make sure to pass 'DomRouterDecorator' as the last decorator of the story.
  const params = useParams<P>();

  /*
    Currently removing support for simulatedHistoricalTime becuase
    the APIs are not available for the full set of historical spotter data.
    // const nowRef = useRef(simulatedHistoricalTime ?? new Date());
  */
  const nowRef = useRef(new Date());
  const timeAtPageLoad = nowRef.current;

  const [query, setQuery] = useQueryParams({
    showSpotters: withDefault(BooleanParam, weatherMapLayerShowSpottersDefault),
    colorSpotters: withDefault(
      BooleanParam,
      weatherMapLayerColorSpottersDefault
    ),
    showTropicalStorms: withDefault(
      BooleanParam,
      weatherMapLayerShowTropicalStormsDefault
    ),
    raster: withDefault(StringParam, weatherMapLayerRasterDefault),
    vector: withDefault(StringParam, weatherMapLayerVectorDefault),
    showVectors: withDefault(BooleanParam, weatherMapLayerVectorDisplayDefault),
    showPressure: withDefault(BooleanParam, weatherMapLayerPressureDefault),
    showPressureCenters: withDefault(
      BooleanParam,
      weatherMapLayerPressureCenterDefault
    ),
    plotQuantity: withDefault(StringParam, plotQuantityDefault),
    currentSimulatorTime: withDefault(NumberParam, timeAtPageLoad.getTime()),
    simulatedHistoricalTime: withDefault(
      StringParam,
      simulatedHistoricalTimeDefault
    ),
  });

  /**
   * Sets the query object with only values that are not equal to their defaults.
   */
  const setManagedQuery = useCallback(
    (
      newQuery: Partial<WeatherQuery>,
      updateType: UrlUpdateType = "replace"
    ) => {
      let result = { ...query };
      if (
        newQuery.raster ||
        newQuery.vector ||
        newQuery.showSpotters !== undefined ||
        newQuery.colorSpotters !== undefined ||
        newQuery.showVectors !== undefined ||
        newQuery.showPressure !== undefined ||
        newQuery.showPressureCenters !== undefined ||
        newQuery.showTropicalStorms !== undefined
      ) {
        result = {
          ...result,
          raster: newQuery.raster || weatherMapLayerRasterDefault,
          vector: newQuery.vector || weatherMapLayerVectorDefault,
          showSpotters:
            newQuery.showSpotters !== undefined
              ? newQuery.showSpotters
              : weatherMapLayerShowSpottersDefault,
          colorSpotters:
            newQuery.colorSpotters !== undefined
              ? newQuery.colorSpotters
              : weatherMapLayerColorSpottersDefault,
          showVectors:
            newQuery.showVectors !== undefined
              ? newQuery.showVectors
              : weatherMapLayerVectorDisplayDefault,
          showPressure:
            newQuery.showPressure !== undefined
              ? newQuery.showPressure
              : weatherMapLayerPressureDefault,
          showPressureCenters:
            newQuery.showPressureCenters !== undefined
              ? newQuery.showPressureCenters
              : weatherMapLayerPressureCenterDefault,
          showTropicalStorms:
            newQuery.showTropicalStorms !== undefined
              ? newQuery.showTropicalStorms
              : weatherMapLayerShowTropicalStormsDefault,
        };
      }
      if (newQuery.plotQuantity) {
        result = {
          ...result,
          plotQuantity: newQuery.plotQuantity,
        };
      }
      if (newQuery.currentSimulatorTime) {
        result = {
          ...result,
          currentSimulatorTime: newQuery.currentSimulatorTime,
        };
      } else {
        result = {
          ...result,
          currentSimulatorTime: timeAtPageLoad.getTime(),
        };
      }
      if (newQuery.simulatedHistoricalTime) {
        result = {
          ...result,
          simulatedHistoricalTime: newQuery.simulatedHistoricalTime,
        };
      }

      // the following lines compare the newly created object against the defaults and only sets the properties
      // that do not equal their defaults.
      setQuery(
        {
          raster:
            result.raster !== weatherMapLayerRasterDefault
              ? (result.raster as string)
              : undefined,
          vector:
            result.vector !== weatherMapLayerVectorDefault
              ? (result.vector as string)
              : undefined,
          showVectors:
            result.showVectors !== weatherMapLayerVectorDisplayDefault
              ? result.showVectors
              : undefined,
          showPressure:
            result.showPressure !== weatherMapLayerPressureDefault
              ? result.showPressure
              : undefined,
          showPressureCenters:
            result.showPressureCenters !== weatherMapLayerPressureCenterDefault
              ? result.showPressureCenters
              : undefined,
          showSpotters:
            result.showSpotters !== weatherMapLayerShowSpottersDefault
              ? result.showSpotters
              : undefined,
          colorSpotters:
            result.colorSpotters !== weatherMapLayerColorSpottersDefault
              ? result.colorSpotters
              : undefined,
          showTropicalStorms:
            result.showTropicalStorms !==
            weatherMapLayerShowTropicalStormsDefault
              ? result.showTropicalStorms
              : undefined,
          plotQuantity:
            result.plotQuantity !== plotQuantityDefault
              ? result.plotQuantity
              : undefined,
          currentSimulatorTime:
            result.currentSimulatorTime !== timeAtPageLoad.getTime()
              ? result.currentSimulatorTime
              : undefined,
          simulatedHistoricalTime:
            result.simulatedHistoricalTime !== simulatedHistoricalTimeDefault
              ? result.simulatedHistoricalTime
              : undefined,
        },
        updateType
      );
    },
    [query, setQuery, timeAtPageLoad]
  );

  const setSimulatedHistoricalTime = useCallback(
    (simulatedHistoricalTime: string) =>
      setManagedQuery({ simulatedHistoricalTime }),
    [setManagedQuery]
  );

  const setWeatherMapLayers = useCallback(
    (newMapLayers: WeatherMapLayers) => {
      setManagedQuery({
        ...newMapLayers,
      });
    },
    [setManagedQuery]
  );

  const setWeatherPlotQuantity = useCallback(
    (newQuantity: PlotValueTypes | PlotParentTypes) => {
      setManagedQuery({ plotQuantity: newQuantity });
    },
    [setManagedQuery]
  );

  const setCurrentSimulatorTime = useCallback(
    (time: Date | undefined) => {
      setManagedQuery({ currentSimulatorTime: time?.getTime() });
    },
    [setManagedQuery]
  );

  const simulatedHistoricalTime = useMemo(
    () =>
      query.simulatedHistoricalTime !== ""
        ? new Date(query.simulatedHistoricalTime)
        : undefined,
    [query.simulatedHistoricalTime]
  );
  const currentSimulatorTime = useMemo(
    () => new Date(query.currentSimulatorTime),
    [query.currentSimulatorTime]
  );

  return useMemo(
    () => ({
      ...params,

      weatherMapLayers: {
        showSpotters: query.showSpotters,
        colorSpotters: query.colorSpotters,
        raster: query.raster as RasterWeatherLayer,
        vector: query.vector as VectorWeatherLayer,
        showVectors: query.showVectors,
        showPressure: query.showPressure,
        showPressureCenters: query.showPressureCenters,
        showTropicalStorms: query.showTropicalStorms,
      },
      weatherPlotQuantity: query.plotQuantity as PlotValueTypes,
      currentSimulatorTime,
      simulatedHistoricalTime,
      timeAtPageLoad,

      setCurrentSimulatorTime,
      setWeatherMapLayers,
      setWeatherPlotQuantity,
      setSimulatedHistoricalTime,
    }),
    [
      params,
      query.showSpotters,
      query.colorSpotters,
      query.raster,
      query.vector,
      query.showVectors,
      query.showPressure,
      query.showPressureCenters,
      query.showTropicalStorms,
      query.plotQuantity,
      currentSimulatorTime,
      timeAtPageLoad,
      simulatedHistoricalTime,
      setCurrentSimulatorTime,
      setWeatherMapLayers,
      setWeatherPlotQuantity,
      setSimulatedHistoricalTime,
    ]
  );
};

export { useAppUrl };
